import React from 'react';

/**
 * ============================================================================
 * FeatureGridBlock - Grille de Fonctionnalités
 * ============================================================================
 * 
 * DESIGN SYSTEM CONTRACT:
 * ✅ width: 100% du parent (le layout contrôle la largeur)
 * ✅ padding interne configurable
 * ✅ styles internes (background, typography)
 * ❌ INTERDIT: max-width basé sur viewport
 * ❌ INTERDIT: width en % du viewport
 * ❌ INTERDIT: margin auto pour contrôler la largeur globale
 * 
 * RÈGLE: Le LAYOUT parent définit la largeur, pas le bloc.
 */
const FeatureGridBlock = ({ block }) => {
  const { features, layout } = block.content;
  
  // Get responsive grid classes from layout
  const getGridClass = () => {
    if (!layout) return 'grid-cols-1 md:grid-cols-3';
    
    const desktop = layout.desktop?.replace('grid-', '');
    const tablet = layout.tablet?.replace('grid-', '');
    const mobile = layout.mobile?.replace('grid-', '');
    
    return `grid-cols-${mobile || 1} md:grid-cols-${tablet || 2} lg:grid-cols-${desktop || 3}`;
  };

  if (!features || features.length === 0) {
    return null;
  }

  return (
    <div 
      className="feature-grid-block"
      data-testid="feature-grid-block"
      data-block-type="feature_grid"
      style={{
        // ============================================================
        // DESIGN SYSTEM CONTRACT: width = 100% du parent
        // Le layout parent contrôle la largeur, pas ce composant
        // ============================================================
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      <div className={`grid ${getGridClass()} gap-6 md:gap-8`}>
        {features.map((feature, index) => (
          <div
            key={index}
            className="flex flex-col items-start gap-3 rounded-lg p-6 bg-gray-50 hover:bg-gray-100 transition-colors"
            data-testid={`feature-${index}`}
          >
            {feature.icon && (
              <div className="text-4xl mb-2">
                {feature.icon}
              </div>
            )}
            <h3 className="text-xl font-heading font-semibold">{feature.title}</h3>
            {feature.description && (
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                {feature.description}
              </p>
            )}
            {feature.bullets && feature.bullets.length > 0 && (
              <ul className="space-y-2 text-sm text-muted-foreground">
                {feature.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-accent-blue mt-1">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeatureGridBlock;